import { useState, useEffect, type ChangeEvent } from "react";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import Modal from "@modalSrc/Modal";

type Supervisor = {
    testInstanceId?: number;
    firstName?: string;
    lastName?: string;
    FirstName?: string;
    LastName?: string;
};

type SupervisorSelectModalProps = {
    isOpen: boolean;
    onClose: () => void;
    companyId?: number | string;
    testInstanceId?: number | string;
    supervisorsInstanceId?: number | string;
};

export default function SupervisorSelectModal({ isOpen, onClose, companyId, testInstanceId, supervisorsInstanceId }: SupervisorSelectModalProps) {
    const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedSupervisorId, setSelectedSupervisorId] = useState<string>("");

    useEffect(() => {
        if (!isOpen) return;
        setSelectedSupervisorId(supervisorsInstanceId ? String(supervisorsInstanceId) : "");
        fetchSupervisors();
    }, [isOpen, companyId, supervisorsInstanceId]);

    if (!isOpen) return null;

    async function fetchSupervisors() {
        if (!companyId) {
            toast.error("Company ID is required");
            return;
        }

        setIsLoading(true);
        try {
            const resp = await RightTimeApi.getAsync(`/Company/${companyId}/Supervisors`);
            if (resp && resp.ok) {
                setSupervisors((resp.jsonData?.supervisors || []) as Supervisor[]);
            } else {
                toast.error("Failed to load supervisors");
                setSupervisors([]);
            }
        } catch (error) {
            console.error("Error loading supervisors:", error);
            toast.error("Error loading supervisors");
            setSupervisors([]);
        } finally {
            setIsLoading(false);
        }
    }

    async function handleSelect() {
        if (!selectedSupervisorId) {
            toast.warning("Please select a supervisor");
            return;
        }

        if (!companyId || !testInstanceId) {
            toast.error("Missing required IDs");
            return;
        }

        setIsSaving(true);
        try {
            const resp = await RightTimeApi.putAsync(
                `/Company/${companyId}/TestInstance/${testInstanceId}/Supervisor/${selectedSupervisorId}`
            );

            if (resp && resp.ok) {
                toast.success("Supervisor assigned successfully");
                handleClose();
            } else {
                toast.error("Failed to assign supervisor");
            }
        } catch (error) {
            console.error("Error assigning supervisor:", error);
            toast.error("Error assigning supervisor");
        } finally {
            setIsSaving(false);
        }
    }

    function handleClose() {
        setSelectedSupervisorId("");
        onClose();
    }


    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Select Supervisor"
            className="supervisorSelectModal"
            size="sm"
            footer={(
                <>
                    <button className="btn btn-secondary" onClick={handleClose}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSelect}
                        disabled={isLoading || isSaving || !selectedSupervisorId}
                    >
                        {isSaving ? "Assigning..." : "Select"}
                    </button>
                </>
            )}
        >
                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <div className="spinner-border" role="status">
                                <span className="sr-only">Loading supervisors...</span>
                            </div>
                        </div>
                    ) : (
                        <div style={{ padding: '20px' }}>
                            <label htmlFor="supervisor-select" style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                                Supervisor
                            </label>
                            <select
                                id="supervisor-select"
                                value={selectedSupervisorId}
                                onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedSupervisorId(e.target.value)}
                                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                            >
                                <option value="">-- Select a Supervisor --</option>
                                {supervisors.map((supervisor, index) => {
                                    const firstName = supervisor?.firstName || supervisor?.FirstName || "";
                                    const lastName = supervisor?.lastName || supervisor?.LastName || "";
                                    const displayName = `${firstName} ${lastName}`.trim();

                                    return (
                                        <option key={testInstanceId || index} value={supervisor?.testInstanceId}>
                                            {displayName || "(Unnamed)"}
                                        </option>
                                    );
                                })}
                            </select>
                            {supervisors.length === 0 && !isLoading && (
                                <p style={{ marginTop: '15px', color: '#666', fontSize: '14px' }}>
                                    No supervisors available.
                                </p>
                            )}
                        </div>
                    )}
        </Modal>
    );
}

